import React, { useEffect } from 'react';
import { Outlet, ScrollRestoration, useLocation, useNavigate } from 'react-router';

import { FacebookCustomEvent, FacebookScrollEvent } from '../analytics/facebook';
import {
  FirebaseCustomEvent,
  FirebaseLinkClickEvent,
  FirebaseScrollEvent,
} from '../analytics/firebase';
import { useNProgress } from '../util/hooks/use-nprogress';
import useAppSelector from '../util/hooks/useAppSelector';
import { registerNavigate } from '../util/routes';

const setHashScrollPosition = (location) => {
  if (!location.hash) return;

  const hashId = location.hash.slice(1);
  const scrollToElement = () => {
    const el = document.getElementById(hashId);
    if (el) {
      el.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    } else {
      setTimeout(scrollToElement, 100);
    }
  };

  requestAnimationFrame(scrollToElement);
};

const RootLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useAppSelector((state) => state.user.currentUser);

  useNProgress();

  useEffect(() => {
    registerNavigate(navigate);
  }, [navigate]);

  useEffect(() => {
    setHashScrollPosition(location);
  }, [location.pathname, location.search, location.hash, location.key]);

  useEffect(() => {
    const handleLinkClickTracking = (event) => {
      setTimeout(() => {
        const anchor = event.target.closest('a');
        if (anchor !== null) {
          const classes = anchor.className
            .split(' ')
            .filter((c) => !c.includes('NamedLink'))
            .map((c) => c.replace(/__.*/g, ''));
          const domain =
            (anchor.href !== '' && new URL(anchor.href)?.hostname) || window.location.hostname;
          const outbound = domain.includes('vansite');
          FirebaseLinkClickEvent.withLinkClasses(classes)
            .withLinkDomain(domain)
            .setOutbound(outbound)
            .logEvent();
          FacebookCustomEvent.withCurrentUserUUID(currentUser?.id?.uuid)
            .withCustomName('LinkClick')
            .withParams({ link_classes: classes, domain, outbound })
            .logEvent();
        }
        const button = event.target.closest('button');
        if (button !== null) {
          const classes = button.className
            .split(' ')
            .filter((c) => !c.includes('NamedLink'))
            .map((c) => c.replace(/__.*/g, ''));
          FirebaseCustomEvent.withCustomName('button-click').withParams({ classes }).logEvent();
          FacebookCustomEvent.withCurrentUserUUID(currentUser?.id?.uuid)
            .withCustomName('ButtonClick')
            .withParams({ button_classes: classes })
            .logEvent();
        }
      }, 0);
    };

    const sentinel = document.createElement('div');
    sentinel.style.cssText = 'position:fixed;width:1px;height:1px;pointer-events:none;bottom:20%;left:0';
    document.body.appendChild(sentinel);

    const scrollObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          FirebaseScrollEvent.logEvent();
          FacebookScrollEvent.withCurrentUserUUID(currentUser?.id?.uuid).logEvent();
          scrollObserver.disconnect();
        }
      },
      { threshold: 0 }
    );
    scrollObserver.observe(sentinel);

    document.addEventListener('click', handleLinkClickTracking);
    return () => {
      scrollObserver.disconnect();
      sentinel.remove();
      document.removeEventListener('click', handleLinkClickTracking);
    };
  }, []);

  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
};

RootLayout.displayName = 'RootLayout';

export default RootLayout;
